import "core-js/modules/es.array.map";
import "core-js/modules/es.object.to-string";
import "core-js/modules/es.regexp.to-string";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import React, { useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import Menu from '@material-ui/core/Menu';
import ButtonSelector from './ButtonSelector';

var placementToAnchorOrigin = function placementToAnchorOrigin(position) {
  var map = {
    'bottom-right': {
      horizontal: 'right',
      vertical: 'bottom'
    },
    'bottom-left': {
      horizontal: 'center',
      vertical: 'bottom'
    },
    bottom: {
      horizontal: 'left',
      vertical: 'bottom'
    },
    'left-end': {
      horizontal: 'left',
      vertical: 'bottom'
    },
    'left-start': {
      horizontal: 'left',
      vertical: 'top'
    },
    left: {
      horizontal: 'left',
      vertical: 'center'
    },
    right: {
      horizontal: 'right',
      vertical: 'center'
    },
    'right-end': {
      horizontal: 'right',
      vertical: 'bottom'
    },
    'right-start': {
      horizontal: 'right',
      vertical: 'top'
    },
    'top-end': {
      horizontal: 'right',
      vertical: 'top'
    },
    'top-start': {
      horizontal: 'left',
      vertical: 'top'
    },
    top: {
      horizontal: 'center',
      vertical: 'top'
    }
  };
  var res = map[position];
  return res || position.top;
};

var useStyles = makeStyles({
  root: {
    display: 'inline'
  }
}, {
  name: 'SxButtonWithMenu'
});
/**
 * Bazova implementace buttonu s dropdown menu.
 *
 * Pokud dostane `icon` vysledkem je IconButton. V opacnem pripade se vyrenderuje Button.
 *
 * > Ostatni props budou predany do buttonu.
 *
 * @component
 */

function ButtonWithMenu(_ref) {
  var notClickMappedChildren = _ref.children,
      className = _ref.className,
      componentJoinId = _ref.id,
      menuPlacement = _ref.menuPlacement,
      rest = _objectWithoutProperties(_ref, ["children", "className", "id", "menuPlacement"]);

  var classes = useStyles();

  var _useRef = useRef(Math.random().toString()),
      randomId = _useRef.current;

  var _useState = useState({
    open: false,
    anchorEl: null
  }),
      _useState2 = _slicedToArray(_useState, 2),
      _useState2$ = _useState2[0],
      anchorEl = _useState2$.anchorEl,
      open = _useState2$.open,
      setState = _useState2[1];

  var handleOpenMenu = function handleOpenMenu(event) {
    setState({
      open: true,
      anchorEl: event.currentTarget
    });
  };

  var handleRequestClose = function handleRequestClose() {
    setState({
      open: false
    });
  };

  var anchorOrigin = useMemo(function () {
    return placementToAnchorOrigin(menuPlacement);
  }, [menuPlacement]);
  var id = componentJoinId || randomId;
  var children = useMemo(function () {
    var wrapItemClickHandler = function wrapItemClickHandler(parentFnc) {
      return function () {
        handleRequestClose();

        if (parentFnc) {
          parentFnc.apply(void 0, arguments);
        }
      };
    };

    return React.Children.map(notClickMappedChildren, function (child) {
      if (child && child.type) {
        var onClick = wrapItemClickHandler(child.props.onClick);

        var props = _extends({}, child.props, {
          onClick: onClick
        });

        return React.cloneElement(child, props);
      }

      return child;
    });
  }, [notClickMappedChildren]);
  return React.createElement("div", {
    className: clsx(classes.root, className)
  }, React.createElement(ButtonSelector, _extends({}, rest, {
    id: id,
    onClick: handleOpenMenu
  })), React.createElement(Menu, {
    id: id,
    anchorEl: anchorEl,
    open: open,
    onClose: handleRequestClose,
    anchorOrigin: anchorOrigin,
    getContentAnchorEl: null
  }, children));
}

ButtonWithMenu.propTypes = {
  /**
   * Polozky menu. V prikladu uvedeny < MenuItem >Ahoj< /MenuItem >
   */
  children: PropTypes.node.isRequired,

  /**
   * Pro css upravy pres withStyle
   */
  className: PropTypes.string,

  /**
   * Barva buttonu.
   */
  color: PropTypes.oneOf(['default', 'primary', 'secondary', 'inherit']),

  /**
   * Pro znefunkcneni buttonu
   */
  disabled: PropTypes.bool,

  /**
   * Icona buttonu jako element. Importovana z m-ui, zapis <Icon />
   */
  icon: PropTypes.element,
  iconColor: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'action', 'error', 'disabled']),

  /**
   * ID elementu. Pokud neni definovano bude pouzito nahodne generovany retezec.
   */
  id: PropTypes.string,

  /**
   * Obsah buttonu. Pokud se ma zobrazit jako IconButton, mela by prop obsahovat string.
   * Pro normalni buttony
   */
  label: PropTypes.node,

  /**
   * Umisteni menu
   *
   * @placementPropType
   */
  menuPlacement: PropTypes.oneOf(['bottom-end', 'bottom-start', 'bottom', 'left-end', 'left-start', 'left', 'right-end', 'right-start', 'right', 'top-end', 'top-start', 'top']),

  /**
   * Umisteni tooltipu
   *
   * @placementPropType
   */
  tooltipPlacement: PropTypes.oneOf(['bottom-end', 'bottom-start', 'bottom', 'left-end', 'left-start', 'left', 'right-end', 'right-start', 'right', 'top-end', 'top-start', 'top']),

  /**
   * Variant zobrazeni klasickeho buttonu.
   *
   * @buttonVariant
   */
  variant: PropTypes.oneOf(['text', 'flat', 'outlined', 'contained', 'raised'])
};
ButtonWithMenu.defaultProps = {
  className: undefined,
  color: undefined,
  disabled: false,
  icon: undefined,
  iconColor: undefined,
  id: null,
  label: null,
  menuPlacement: 'top-start',
  tooltipPlacement: 'bottom',
  variant: 'text'
};
export default ButtonWithMenu;